import { BrowserMediaRecorder } from '../../adapters/browser/audio/BrowserMediaRecorder';
import { BrowserAudioPlayback } from '../../adapters/browser/audio/BrowserAudioPlayback';
import { GaiaSpeechToTextHttp } from '../../adapters/gaia/speech/GaiaSpeechToTextHttp';
import { GaiaTextToSpeechHttp } from '../../adapters/gaia/speech/GaiaTextToSpeechHttp';
import type { ExperienceSession } from './ExperienceSession';
import type { SessionEnvironment } from './buildSession';
import { createScriptedSpeechPorts } from './FakeSpeechPorts';
import type { SpeechPorts } from '../../ports/SpeechPorts';
import { fakeModeEnabled } from './scriptedPorts';
import { flagEnabled, speechRealEnabled } from './pageFlags';
import { MIC_PREPROMPT_ACK_KEY, VoiceTurn } from './VoiceTurn';
import type { PermissionAnswer } from './VoiceTurn';

/** Builds the spoken turn for a page run: scripted under "?fake=1", real otherwise. */
export function buildVoiceTurn(session: ExperienceSession, env: SessionEnvironment): VoiceTurn {
  return buildVoiceTurnWithPorts(session, env).voice;
}

/**
 * Same as `buildVoiceTurn`, but also returns the speech ports it built the turn
 * with, so tests can assert which concrete classes were wired in. Under plain
 * "?fake=1" the ports are scripted; under "?fake=1&speech=real" the session
 * stays scripted but the four speech ports (microphone capture, speech-to-text,
 * text-to-speech, and playback) are the real browser/gateway ones, so real
 * audio runs end to end against a scripted session.
 */
export function buildVoiceTurnWithPorts(
  session: ExperienceSession,
  env: SessionEnvironment,
): { voice: VoiceTurn; ports: SpeechPorts } {
  const fake = fakeModeEnabled(env.search);
  const useScriptedSpeech = fake && !speechRealEnabled(env.search);
  const ports = useScriptedSpeech ? createScriptedSpeechPorts() : realSpeechPorts(env.baseUrl);
  const voice = useScriptedSpeech
    ? new VoiceTurn(session, ports, {
        storage: memoryStorage(flagEnabled(env.search, 'micPrompt') ? {} : { [MIC_PREPROMPT_ACK_KEY]: 'true' }),
      })
    : new VoiceTurn(session, ports, {
        storage: safeLocalStorage(),
        queryPermission: browserPermissionQuery(),
        isSupported: () => BrowserMediaRecorder.isSupported(),
      });
  if (env.dev) window.__voice = voice;
  return { voice, ports };
}

function realSpeechPorts(baseUrl: string): SpeechPorts {
  return {
    capture: new BrowserMediaRecorder(),
    speechToText: new GaiaSpeechToTextHttp(baseUrl),
    textToSpeech: new GaiaTextToSpeechHttp(baseUrl),
    playback: new BrowserAudioPlayback(),
  };
}

/** Asks the browser whether the microphone is already granted, denied, or still to be asked. */
export function browserPermissionQuery(): () => Promise<PermissionAnswer> {
  return async () => {
    try {
      const permissions = (navigator as Navigator & { permissions?: Permissions }).permissions;
      if (!permissions?.query) return 'unknown';
      const status = await permissions.query({ name: 'microphone' as PermissionName });
      return status.state as PermissionAnswer;
    } catch {
      return 'unknown';
    }
  };
}

function memoryStorage(initial: Record<string, string>): Pick<Storage, 'getItem' | 'setItem'> {
  const map = new Map(Object.entries(initial));
  return { getItem: (k) => map.get(k) ?? null, setItem: (k, v) => { map.set(k, v); } };
}

function safeLocalStorage(): Pick<Storage, 'getItem' | 'setItem'> | undefined {
  try { return window.localStorage; } catch { return undefined; }
}

declare global {
  interface Window { __voice?: VoiceTurn }
}
